'use client';

import { useState } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, Cell
} from 'recharts';
import Icon from '@/components/ui/AppIcon';

interface ModelVariable {
  id: string;
  name: string;
  weight: number;
  category: string;
  enabled: boolean;
}

interface BacktestResult {
  period: string;
  accuracy: number;
  roi: number;
  picks: number;
}

interface SavedModel {
  id: number;
  name: string;
  accuracy: number;
  roi: number;
  picks: number;
  created: string;
  variables: number;
  isPublic: boolean;
}

const DEFAULT_VARIABLES: ModelVariable[] = [
  { id: 'xgf', name: 'Expected Goals For (xGF)', weight: 25, category: 'Possession', enabled: true },
  { id: 'xga', name: 'Expected Goals Against (xGA)', weight: 20, category: 'Possession', enabled: true },
  { id: 'sv_pct', name: 'Save Percentage', weight: 18, category: 'Goalie', enabled: true },
  { id: 'corsi', name: 'Corsi For %', weight: 15, category: 'Possession', enabled: true },
  { id: 'pp_pct', name: 'Power Play %', weight: 10, category: 'Special Teams', enabled: true },
  { id: 'pk_pct', name: 'Penalty Kill %', weight: 8, category: 'Special Teams', enabled: true },
  { id: 'fatigue', name: 'Fatigue Index', weight: 4, category: 'Context', enabled: true },
  { id: 'travel', name: 'Travel Factor', weight: 0, category: 'Context', enabled: false },
  { id: 'h2h', name: 'Head-to-Head History', weight: 0, category: 'Historical', enabled: false },
  { id: 'momentum', name: 'Momentum Score', weight: 0, category: 'Context', enabled: false },
];

const BACKTEST_DATA: BacktestResult[] = [
  { period: 'Oct', accuracy: 58.2, roi: 4.2, picks: 42 },
  { period: 'Nov', accuracy: 61.5, roi: 6.8, picks: 48 },
  { period: 'Dec', accuracy: 63.8, roi: 8.4, picks: 52 },
  { period: 'Jan', accuracy: 65.2, roi: 9.1, picks: 55 },
  { period: 'Fév', accuracy: 64.1, roi: 8.7, picks: 49 },
  { period: 'Mar', accuracy: 67.3, roi: 11.2, picks: 58 },
];

const PUBLIC_MODELS: SavedModel[] = [
  { id: 1, name: 'xG Master v2', accuracy: 66.8, roi: 10.4, picks: 342, created: '2025-11-01', variables: 8, isPublic: true },
  { id: 2, name: 'Sharp Goalie Model', accuracy: 71.2, roi: 14.8, picks: 198, created: '2025-10-15', variables: 5, isPublic: true },
  { id: 3, name: 'Possession Dominance', accuracy: 63.5, roi: 7.2, picks: 289, created: '2025-12-01', variables: 6, isPublic: true },
];

export default function CustomModelsClient() {
  const [variables, setVariables] = useState<ModelVariable[]>(DEFAULT_VARIABLES);
  const [modelName, setModelName] = useState('Mon Modèle Custom');
  const [saved, setSaved] = useState<SavedModel[]>([]);
  const [activeTab, setActiveTab] = useState<'builder' | 'backtest' | 'compare'>('builder');
  const [saving, setSaving] = useState(false);

  const totalWeight = variables.filter(v => v.enabled).reduce((s, v) => s + v.weight, 0);

  const updateWeight = (id: string, weight: number) => {
    setVariables(prev => prev.map(v => v.id === id ? { ...v, weight } : v));
  };

  const toggleVar = (id: string) => {
    setVariables(prev => prev.map(v => v.id === id ? { ...v, enabled: !v.enabled, weight: !v.enabled ? 10 : 0 } : v));
  };

  const saveModel = () => {
    setSaving(true);
    setTimeout(() => {
      const newModel: SavedModel = {
        id: Date.now(), name: modelName,
        accuracy: 62 + Math.random() * 8,
        roi: 5 + Math.random() * 8,
        picks: 0, created: new Date().toISOString().split('T')[0],
        variables: variables.filter(v => v.enabled).length,
        isPublic: false,
      };
      setSaved(prev => [...prev, newModel]);
      setSaving(false);
    }, 800);
  };

  const enabledVars = variables.filter(v => v.enabled);
  const weightData = enabledVars.map(v => ({ name: v.name.split(' ')[0], weight: v.weight }));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500/20 to-blue-500/20 border border-indigo-500/30 flex items-center justify-center">
            <Icon name="CpuChipIcon" size={20} className="text-indigo-400" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-foreground">Custom AI Models</h1>
            <p className="text-xs text-muted-foreground font-mono-data">Pondération Custom · Backtesting · Comparaison Modèles</p>
          </div>
        </div>
        <div className="flex items-center gap-1 bg-card border border-border rounded-xl p-1">
          {(['builder', 'backtest', 'compare'] as const).map(t => (
            <button key={t} onClick={() => setActiveTab(t)}
              className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-all ${activeTab === t ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}>
              {t === 'builder' ? 'Builder' : t === 'backtest' ? 'Backtesting' : 'Comparaison'}
            </button>
          ))}
        </div>
      </div>

      {/* Builder */}
      {activeTab === 'builder' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 space-y-4">
            {/* Model name */}
            <div className="bg-card border border-border rounded-xl p-4">
              <label className="text-2xs text-muted-foreground font-mono-data block mb-2">NOM DU MODÈLE</label>
              <input value={modelName} onChange={e => setModelName(e.target.value)}
                className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground" />
            </div>

            {/* Variables */}
            <div className="bg-card border border-border rounded-xl p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-foreground">Variables & Pondérations</h3>
                <span className={`text-xs font-mono-data ${Math.abs(totalWeight - 100) < 1 ? 'text-positive' : 'text-negative'}`}>
                  Total: {totalWeight}% {Math.abs(totalWeight - 100) < 1 ? '✓' : '≠ 100%'}
                </span>
              </div>
              <div className="space-y-3">
                {variables.map(v => (
                  <div key={v.id} className={`p-3 rounded-xl border transition-all ${v.enabled ? 'bg-background border-border' : 'bg-card border-border opacity-50'}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <button onClick={() => toggleVar(v.id)}
                          className={`w-4 h-4 rounded border flex items-center justify-center transition-all ${v.enabled ? 'bg-primary border-primary' : 'border-border'}`}>
                          {v.enabled && <Icon name="CheckIcon" size={10} className="text-primary-foreground" />}
                        </button>
                        <span className="text-sm text-foreground">{v.name}</span>
                        <span className="text-2xs px-1.5 py-0.5 rounded bg-card border border-border text-muted-foreground">{v.category}</span>
                      </div>
                      <span className="text-sm font-mono-data text-primary">{v.weight}%</span>
                    </div>
                    {v.enabled && (
                      <input type="range" min="0" max="40" value={v.weight}
                        onChange={e => updateWeight(v.id, parseInt(e.target.value))}
                        className="w-full accent-primary" />
                    )}
                  </div>
                ))}
              </div>
            </div>

            <button onClick={saveModel} disabled={saving || Math.abs(totalWeight - 100) > 5}
              className="w-full py-3 rounded-xl bg-primary hover:bg-primary/90 disabled:opacity-50 text-primary-foreground text-sm font-semibold transition-all flex items-center justify-center gap-2">
              {saving ? <><Icon name="ArrowPathIcon" size={16} className="animate-spin" /> Sauvegarde...</> : <><Icon name="BookmarkIcon" size={16} /> Sauvegarder le Modèle</>}
            </button>
          </div>

          {/* Weight chart */}
          <div className="space-y-4">
            <div className="bg-card border border-border rounded-xl p-4">
              <h3 className="text-sm font-semibold text-foreground mb-3">Distribution des Poids</h3>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={weightData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 9, fill: '#888' }} unit="%" domain={[0, 40]} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 9, fill: '#888' }} width={55} />
                  <Tooltip contentStyle={{ background: '#1a1a2e', border: '1px solid #333', borderRadius: 8 }} formatter={(v: number) => [`${v}%`, 'Poids']} />
                  <Bar dataKey="weight" radius={[0, 4, 4, 0]}>
                    {weightData.map((_, i) => <Cell key={i} fill={`hsl(${200 + i * 20}, 70%, 60%)`} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            {saved.length > 0 && (
              <div className="bg-card border border-border rounded-xl p-4">
                <h3 className="text-sm font-semibold text-foreground mb-3">Mes Modèles Sauvegardés</h3>
                <div className="space-y-2">
                  {saved.map(m => (
                    <div key={m.id} className="p-2.5 rounded-lg bg-background border border-border">
                      <div className="text-sm font-medium text-foreground">{m.name}</div>
                      <div className="flex gap-3 mt-1 text-xs text-muted-foreground">
                        <span>{m.variables} vars</span>
                        <span className="text-positive">{m.accuracy.toFixed(1)}% acc.</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Backtest */}
      {activeTab === 'backtest' && (
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            {[
              { label: 'Précision Moy.', value: '63.4%', color: 'text-positive' },
              { label: 'ROI Moyen', value: '+8.1%', color: 'text-yellow-400' },
              { label: 'Total Picks', value: '304', color: 'text-primary' },
            ].map((s, i) => (
              <div key={i} className="bg-card border border-border rounded-xl p-4 text-center">
                <div className={`text-2xl font-bold font-mono-data ${s.color}`}>{s.value}</div>
                <div className="text-xs text-muted-foreground mt-1">{s.label}</div>
              </div>
            ))}
          </div>
          <div className="bg-card border border-border rounded-xl p-4">
            <h3 className="text-sm font-semibold text-foreground mb-3">Performance Historique — Saison 2025-26</h3>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={BACKTEST_DATA}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="period" tick={{ fontSize: 11, fill: '#888' }} />
                <YAxis yAxisId="acc" domain={[55, 75]} tick={{ fontSize: 10, fill: '#888' }} unit="%" />
                <YAxis yAxisId="roi" orientation="right" domain={[0, 15]} tick={{ fontSize: 10, fill: '#888' }} unit="%" />
                <Tooltip contentStyle={{ background: '#1a1a2e', border: '1px solid #333', borderRadius: 8 }} />
                <Line yAxisId="acc" type="monotone" dataKey="accuracy" name="Précision" stroke="#22c55e" strokeWidth={2} dot={{ fill: '#22c55e', r: 4 }} />
                <Line yAxisId="roi" type="monotone" dataKey="roi" name="ROI" stroke="#f59e0b" strokeWidth={2} dot={{ fill: '#f59e0b', r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Compare */}
      {activeTab === 'compare' && (
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-foreground">Comparaison avec Modèles Publics</h3>
          <div className="space-y-3">
            {PUBLIC_MODELS.map(model => (
              <div key={model.id} className="bg-card border border-border rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-foreground">{model.name}</span>
                      <span className="text-2xs px-1.5 py-0.5 rounded bg-primary/10 text-primary border border-primary/20">PUBLIC</span>
                    </div>
                    <span className="text-xs text-muted-foreground">{model.variables} variables · {model.picks} picks</span>
                  </div>
                  <button className="text-xs text-primary hover:text-primary/80 transition-colors flex items-center gap-1">
                    <Icon name="ArrowDownTrayIcon" size={12} />
                    Cloner
                  </button>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { label: 'Précision', value: `${model.accuracy.toFixed(1)}%`, color: 'text-positive' },
                    { label: 'ROI', value: `+${model.roi.toFixed(1)}%`, color: 'text-yellow-400' },
                    { label: 'Picks', value: model.picks, color: 'text-primary' },
                  ].map((s, i) => (
                    <div key={i} className="bg-background rounded-lg p-2.5 text-center">
                      <div className={`text-base font-bold font-mono-data ${s.color}`}>{s.value}</div>
                      <div className="text-2xs text-muted-foreground">{s.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
